import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, Mail, Phone, MapPin, Calendar, User } from 'lucide-react';
import client from '@/lib/api';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import { CardSkeleton } from '@/components/Skeletons';
import { formatDate, formatNumber } from '@/lib/format';

export default function WarehouseDetail() {
  const { id } = useParams();
  const q = useQuery({
    queryKey: ['warehouse', id],
    queryFn: () => client.get(`/warehouses/${id}`).then((r) => r.data.data),
  });

  return (
    <div data-testid="wh-detail-page">
      <Link to="/admin/warehouses" className="text-xs text-zinc-500 hover:text-zinc-950 flex items-center gap-1 mb-4"><ChevronLeft className="w-3 h-3" /> Back to warehouses</Link>
      {q.isLoading ? (
        <div className="space-y-4"><CardSkeleton height={80} /><CardSkeleton height={200} /></div>
      ) : !q.data ? (
        <div className="text-sm text-zinc-500">Not found.</div>
      ) : (
        <>
          <PageHeader
            title={q.data.name}
            breadcrumbs={['Warehouses', q.data.name]}
            actions={<StatusBadge status={q.data.status} />}
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-lg p-6">
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-4">Contact & business info</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8">
                <Row icon={User} label="Contact person" value={q.data.contactPersonName} />
                <Row icon={Mail} label="Email" value={q.data.email} mono />
                <Row icon={Phone} label="Mobile" value={q.data.mobile} mono />
                <Row icon={Phone} label="Alt. contact" value={q.data.alternateContactNumber || '—'} mono />
                <Row icon={MapPin} label="Address" value={q.data.address} span2 />
              </div>
              <div className="mt-6 pt-6 border-t border-zinc-100">
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-4">GST & compliance</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-8">
                  <Row label="Company name" value={q.data.profile?.companyName || '—'} />
                  <Row label="GST number" value={q.data.profile?.gstNumber || '—'} mono />
                  <Row label="PAN number" value={q.data.profile?.panNumber || '—'} mono />
                  <Row label="City / State" value={`${q.data.profile?.city || '—'}, ${q.data.profile?.state || '—'}`} />
                  <Row label="Pincode" value={q.data.profile?.pincode || '—'} mono />
                  <Row label="Country" value={q.data.profile?.country || '—'} />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white border border-zinc-200 rounded-lg p-6">
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">Customers</div>
                <div className="font-display text-3xl font-bold tabular-nums">{formatNumber(q.data.customerCount ?? 0)}</div>
                <div className="text-xs text-zinc-500 mt-1">Active tenants of this warehouse</div>
              </div>
              <div className="bg-white border border-zinc-200 rounded-lg p-6">
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-3">Free trial</div>
                {q.data.freeTrialEnabled ? (
                  <>
                    <div className="text-sm font-mono">Start: {formatDate(q.data.trialStartDate)}</div>
                    <div className="text-sm font-mono">End: {formatDate(q.data.trialEndDate)}</div>
                  </>
                ) : (
                  <div className="text-sm text-zinc-500">Not on trial.</div>
                )}
              </div>
              <div className="bg-white border border-zinc-200 rounded-lg p-6">
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-3">Admins</div>
                {(q.data.admins || []).length === 0 ? (
                  <div className="text-sm text-zinc-500">No admin users.</div>
                ) : q.data.admins.map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-sm py-1.5">
                    <div>
                      <div className="text-zinc-950">{a.fullName || a.email}</div>
                      <div className="text-xs font-mono text-zinc-500">{a.email}</div>
                    </div>
                    <StatusBadge status={a.status} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, mono, span2 }) {
  return (
    <div className={span2 ? 'md:col-span-2' : ''}>
      <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-0.5">{label}</div>
      <div className={`text-sm ${mono ? 'font-mono' : ''} text-zinc-950 flex items-center gap-2`}>
        {Icon && <Icon className="w-3.5 h-3.5 text-zinc-400 shrink-0" />}
        <span>{value}</span>
      </div>
    </div>
  );
}
